import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Upload, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SOURCES, SOURCE_META, DIAMETERS, TUBE_TYPES } from "@/lib/funnel";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const empty = {
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  source: "website",
  source_detail: "",
  product_requirement: "",
  quantity: "",
  specifications: "",
  estimated_value: "",
  city: "",
  diameter: "none",
  tube_type: "none",
  priority: "medium",
  assigned_to: "unassigned",
};

export default function AddLeadDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(empty);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [images, setImages] = useState([]);
  const fileRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setForm(empty);
      setImages([]);
      api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
    }
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const readAsDataUrl = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const addImageFiles = async (files) => {
    const valid = Array.from(files || []).filter((f) => /^image\/(png|jpe?g|webp)$/.test(f.type));
    const rejected = Array.from(files || []).length - valid.length;
    if (rejected > 0) toast.error("Some files were skipped (use PNG, JPG or WEBP)");
    if (!valid.length) return;
    try {
      const urls = await Promise.all(valid.map(readAsDataUrl));
      setImages((prev) => [...prev, ...urls].slice(0, 8));
    } catch {
      toast.error("Couldn't read the image(s)");
    }
  };

  const removeImage = (idx) => setImages((prev) => prev.filter((_, i) => i !== idx));

  const runExtraction = async () => {
    if (!images.length) return;
    setExtracting(true);
    try {
      const { data } = await api.post("/leads/extract-image", { images });
      const dia = DIAMETERS.includes(data.diameter) ? data.diameter : "none";
      const tt = TUBE_TYPES.includes(data.tube_type) ? data.tube_type : "none";
      setForm((f) => ({
        ...f,
        company_name: data.company_name || f.company_name,
        contact_name: data.contact_name || f.contact_name,
        email: data.email || f.email,
        phone: data.phone || f.phone,
        city: data.city || f.city,
        product_requirement: data.product_requirement || f.product_requirement,
        quantity: data.quantity || f.quantity,
        estimated_value: data.estimated_value != null ? String(data.estimated_value) : f.estimated_value,
        specifications: data.specifications || f.specifications,
        diameter: dia !== "none" ? dia : f.diameter,
        tube_type: tt !== "none" ? tt : f.tube_type,
        source: "indiamart",
      }));
      toast.success(`Details extracted from ${images.length} screenshot${images.length > 1 ? "s" : ""} — review & edit before saving`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't read those screenshots");
    } finally {
      setExtracting(false);
    }
  };

  const handleImage = (e) => {
    const files = e.target.files;
    addImageFiles(files);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) addImageFiles(e.dataTransfer.files);
  };

  // Paste a snip directly (Ctrl/Cmd+V) anywhere while the dialog is open — adds to the list.
  useEffect(() => {
    if (!open) return;
    const onPaste = (e) => {
      const items = e.clipboardData?.items || [];
      const files = [];
      for (const it of items) {
        if (it.type && it.type.startsWith("image/")) {
          const file = it.getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length) { e.preventDefault(); addImageFiles(files); }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
    // eslint-disable-next-line
  }, [open]);

  const submit = async (force = false) => {
    if (!form.company_name.trim()) {
      toast.error("Company name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : null,
        assigned_to: form.assigned_to === "unassigned" ? null : form.assigned_to,
        diameter: form.diameter === "none" ? null : form.diameter,
        tube_type: form.tube_type === "none" ? null : form.tube_type,
        source_detail: form.source === "other" ? (form.source_detail || null) : null,
      };
      const { data } = await api.post(`/leads${force ? "?force=true" : ""}`, payload);
      toast.success("Lead created");
      onOpenChange(false);
      onCreated && onCreated();
      navigate(`/leads/${data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.code === "duplicate_lead") {
        toast.warning(detail.message, {
          duration: 10000,
          action: {
            label: "View existing",
            onClick: () => { onOpenChange(false); navigate(`/leads/${detail.existing_lead_id}`); },
          },
          cancel: { label: "Add anyway", onClick: () => submit(true) },
        });
      } else {
        toast.error(formatApiErrorDetail(detail));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="add-lead-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Add New Lead</DialogTitle>
        </DialogHeader>
        <div className="space-y-3.5 py-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            multiple
            onChange={handleImage}
            className="hidden"
            data-testid="lead-image-input"
          />
          <div
            onClick={() => !extracting && fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            data-testid="extract-image-btn"
            className={`w-full cursor-pointer flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-5 text-sm font-medium transition-colors ${
              dragOver ? "border-blue-500 bg-blue-100/70" : "border-blue-300 bg-blue-50/60 hover:border-blue-400 hover:bg-blue-50"
            } ${extracting ? "opacity-70 pointer-events-none" : ""}`}
          >
            <span className="flex items-center gap-2 text-blue-700"><Upload className="h-4 w-4" /> Drag & drop, paste (Ctrl/Cmd+V), or click to add screenshots</span>
            <span className="text-xs font-normal text-slate-400">Add multiple snips for a long email · IndiaMART/WhatsApp/email · PNG/JPG/WEBP</span>
          </div>

          {images.length > 0 && (
            <div className="space-y-2" data-testid="screenshot-tray">
              <div className="flex flex-wrap gap-2">
                {images.map((src, i) => (
                  <div key={i} className="relative group" data-testid={`screenshot-thumb-${i}`}>
                    <img src={src} alt={`screenshot ${i + 1}`} className="h-16 w-16 object-cover rounded-md border border-slate-200" />
                    <span className="absolute -top-1.5 -left-1.5 h-4 w-4 rounded-full bg-slate-700 text-white text-[10px] flex items-center justify-center">{i + 1}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeImage(i); }}
                      data-testid={`remove-screenshot-${i}`}
                      className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </div>
                ))}
              </div>
              <Button
                type="button"
                onClick={runExtraction}
                disabled={extracting}
                data-testid="run-extraction-btn"
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {extracting ? (
                  <span className="flex items-center gap-2"><Sparkles className="h-4 w-4 animate-pulse" /> Reading {images.length} screenshot{images.length > 1 ? "s" : ""}…</span>
                ) : (
                  <span className="flex items-center gap-2"><Sparkles className="h-4 w-4" /> Extract details from {images.length} screenshot{images.length > 1 ? "s" : ""}</span>
                )}
              </Button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Company Name *</Label>
              <Input
                data-testid="lead-company-input"
                value={form.company_name}
                onChange={(e) => set("company_name", e.target.value)}
                placeholder="Acme Cosmetics Pvt Ltd"
              />
            </div>
            <div>
              <Label className="text-xs">Contact Name</Label>
              <Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Phone</Label>
              <Input data-testid="lead-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91..." />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Email</Label>
              <Input value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Source</Label>
              <Select value={form.source} onValueChange={(v) => set("source", v)}>
                <SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SOURCES.map((s) => (
                    <SelectItem key={s} value={s}>{SOURCE_META[s].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.source === "other" && (
                <Input
                  className="mt-2"
                  data-testid="lead-source-detail-input"
                  placeholder="Type the actual source (e.g. Trade fair, Referral…)"
                  value={form.source_detail}
                  onChange={(e) => set("source_detail", e.target.value)}
                />
              )}
            </div>
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Product Requirement</Label>
              <Input
                value={form.product_requirement}
                onChange={(e) => set("product_requirement", e.target.value)}
                placeholder="D35 laminated tubes, matte finish"
              />
            </div>
            <div>
              <Label className="text-xs">Quantity</Label>
              <Input value={form.quantity} onChange={(e) => set("quantity", e.target.value)} placeholder="50,000 pcs" />
            </div>
            <div>
              <Label className="text-xs">Est. Value (INR)</Label>
              <Input
                type="number"
                value={form.estimated_value}
                onChange={(e) => set("estimated_value", e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Diameter (Dia)</Label>
              <Select value={form.diameter} onValueChange={(v) => set("diameter", v)}>
                <SelectTrigger data-testid="lead-diameter-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not specified</SelectItem>
                  {DIAMETERS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tube Type</Label>
              <Select value={form.tube_type} onValueChange={(v) => set("tube_type", v)}>
                <SelectTrigger data-testid="lead-tubetype-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not specified</SelectItem>
                  {TUBE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">City / Location</Label>
              <Input
                data-testid="lead-city-input"
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
                placeholder="Mumbai, Daman, Baddi…"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Specifications / Notes</Label>
              <Textarea
                rows={2}
                value={form.specifications}
                onChange={(e) => set("specifications", e.target.value)}
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Assign To</Label>
              <Select value={form.assigned_to} onValueChange={(v) => set("assigned_to", v)}>
                <SelectTrigger data-testid="lead-assign-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="submit-lead-btn" onClick={() => submit()} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            {saving ? "Saving..." : "Create Lead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
